import { Component, OnInit, signal, computed, model, effect, untracked } from "@angular/core";
import { CommonModule, formatDate, TitleCasePipe } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { Area, JobIssue, UserVendorContact } from "../../../../../core/database/db";
import ScheduleDetailHeader from "../../../../../domains/jobs/components/shared/schedule-detail-header/schedule-detail-header.component";
import { HeaderSectionComponent } from "../../../components/header/header.component";
import { ContactChangeEvent, IssueItemComponent } from "./components/issue-item/issue-item.component";
import { SkeletonModule } from "primeng/skeleton";
import { DividerModule } from "primeng/divider";
import { JobIssueService } from "../../../../../domains/jobIssues/services/job-issue-service";
import { UiToastService } from "../../../../../shared/components/toast/ui-toast.service";
import { FilterPanelComponent } from "../../../../../shared/components/filter-panel/filter-panel.component";
import { FilterService, ActiveFilterChip, DUE_FILTER_ID, DueFilterValue } from "./services/filter.service";
import { FilterSelection } from "../../../../../shared/components/filter-panel/filter-panel.model";
import { AssignRoomBottomSheetComponent } from "./components/assign-rooms/assign-room-bottom-sheet.component";
import { TabBarComponent, TabOption } from "../../../../../shared/components/tab-bar/tab-bar.component";
import { AuthService } from "../../../../../core/authentication/auth.service";
import { ExtendedRouter } from "../../../../../shared/services/extended-router.service";
import { AddNewItemBottomSheetComponent } from "./components/add-new-item/add-new-item-bottom-sheet.component";
import { getBqStepDisplay } from "../../../../../domains/jobs/models/build-quality-step-display";
import { OpenItemsCsvService, OpenItemsCsvRow } from "./services/open-items-csv.service";
import { AssignItemsToTradeBottomSheetComponent } from "./components/assign-items-to-trade/assign-items-to-trade-bottom-sheet.component";
import { OpenItemsDataService } from "../services/open-items-data.service";
import { JobIssueVendorWithDetails, JobIssueWithDetails } from "../../../../../domains/jobIssues/models/JobIssueWithDetails";
import { HorizontalButton, HorizontalButtonGroupComponent } from "../../../../../shared/components/horizontal-button-group/horizontal-button-group.component";
import { StandardBottomSheetComponent } from "../../../../../shared/components/standard-bottom-sheet/standard-bottom-sheet.component";
import { VerticalButton, VerticalButtonGroupComponent } from "../../../../../shared/components/vertical-button-group/vertical-button-group.component";
import { ChipModule } from "primeng/chip";
import { ButtonModule } from "primeng/button";
import { VendorDTO } from "../../../../../domains/vendors/models/VendorDTO";
import { isValidEmail } from "../../../../../shared/utils/email-helper";
import { DataUpdatedFadeDirective } from "../../../../../shared/directives/data-updated-fade.directive";
import { BottomSheetComponent } from "../../../../../shared/components/bottom-sheet/bottom-sheet.component";

interface SendToVendorGroup {
  issues: JobIssueWithDetails[];
  vendorAssignment: JobIssueVendorWithDetails;
  contacts: UserVendorContact[];
}

interface SendToVendorResult {
  vendorId: number,
  success: boolean,
  vendorName: string,
  contactCount: number
}

export enum OpenItemSelection {
  Room = "Room",
  Vendor = "Vendor",
  Type = "Type"
}

@Component({
  selector: "app-open-items",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ScheduleDetailHeader,
    HeaderSectionComponent,
    IssueItemComponent,
    SkeletonModule,
    AssignRoomBottomSheetComponent,
    DividerModule,
    TabBarComponent,
    AddNewItemBottomSheetComponent,
    AssignItemsToTradeBottomSheetComponent,
    HorizontalButtonGroupComponent,
    StandardBottomSheetComponent,
    VerticalButtonGroupComponent,
    FilterPanelComponent,
    ChipModule,
    DataUpdatedFadeDirective,
    BottomSheetComponent,
    ButtonModule
  ],
  providers: [TitleCasePipe],
  templateUrl: "./open-items.component.html",
  styleUrls: ["./open-items.component.scss"]
})
export class OpenItemsComponent implements OnInit {
  constructor(
    private route: ActivatedRoute,
    private router: ExtendedRouter,
    private jobIssueService: JobIssueService,
    private toastService: UiToastService,
    public filterService: FilterService,
    private authService: AuthService,
    private openItemsCsvService: OpenItemsCsvService,
    protected openItemsDataService: OpenItemsDataService,
    private titleCasePipe: TitleCasePipe
  ) {

    // Read the target issue from the navigation state. Fall back to history.state in case
    // getCurrentNavigation() has already been cleared by the time this component is created.
    const navigationState = (this.router.getCurrentNavigation()?.extras.state
      ?? (typeof window !== "undefined" ? window.history.state : null)) as Record<string, unknown> | null;
    this.pendingHighlightIssueId = (navigationState?.["highlightIssueId"] as number | undefined) ?? null;

    effect(() => {
      if (this.loading()) {
        this.checkedContactMapPerVendor.set({});
      } else {
        this.checkedContactMapPerVendor.set(this.issuesWithDetails()
          .flatMap(issue => issue.assignedVendors)
          .reduce((vendorMap, v) => {
            return {
              ...vendorMap,
              [v.vendorDetails.id]: v.vendorDetails.userVendorContacts,
            };
          }, {} as Record<number, UserVendorContact[]>)
        );

        if (this.pendingHighlightIssueId !== null) {
          this.scrollToAndHighlightIssue(this.pendingHighlightIssueId);
          this.pendingHighlightIssueId = null;
        }
      }
    });

    // Drop checked state for issues that fall out of the filtered set. issueCheckedMap is read via
    // untracked so this effect only reacts to filteredIssues changing, not to its own writes.
    effect(() => {
      const filteredIssues = this.filteredIssues();
      untracked(() => this.pruneStaleCheckedIssues(filteredIssues));
    });
  }

  private pendingHighlightIssueId: number | null = null;

  tabOptions: TabOption<OpenItemSelection>[] = [
    { label: "Room", value: OpenItemSelection.Room },
    { label: "Vendor", value: OpenItemSelection.Vendor },
    { label: "Type", value: OpenItemSelection.Type },
  ];

  currentTab = signal(OpenItemSelection.Room);
  isSendingToVendor = signal(false);
  isMarkingComplete = signal(false);

  areIssuesCollapsed = signal<boolean>(false);
  issueCheckedMap = signal<Record<string, boolean>>({});
  issuesExpandedMap = signal<Record<string, boolean>>({});
  checkedContactMapPerVendor = signal<Record<number, UserVendorContact[]>>({});
  editTextIssue = signal<JobIssueWithDetails | null>(null);

  showMoreActionsSheet = model<boolean>(false);
  showAssignRoomSheet = model<boolean>(false);
  showAddNewItemSheet = model<boolean>(false);
  showAssignItemsToTradeSheet = model<boolean>(false);
  showFilterPanelSheet = model<boolean>(false);
  isItemsMenuOpen = false;

  spotlightIssueId = signal<number | null>(null);

  lot = computed(() => this.openItemsDataService.lot() ?? (() => {
    this.goBack();
    throw new Error("Lot data is not available in OpenItemsDataService.");
  })());
  jobs = computed(() => this.openItemsDataService.jobs() ?? (() => {
    this.goBack();
    throw new Error("Job data is not available in OpenItemsDataService.");
  })());
  areas = computed(() => this.openItemsDataService.areas() ?? (() => {
    this.goBack();
    throw new Error("Area data is not available in OpenItemsDataService.");
  })());
  media = computed(() => this.openItemsDataService.media() ?? (() => {
    this.goBack();
    throw new Error("Media data is not available in OpenItemsDataService.");
  })());
  actionableJobIssues = computed(() => this.openItemsDataService.actionableJobIssues() ?? (() => {
    this.goBack();
    throw new Error("Job issues data is not available in OpenItemsDataService.");
  })());
  allJobIssuesForLot = computed(() => this.openItemsDataService.allJobIssuesForLot() ?? (() => {
    this.goBack();
    throw new Error("All job issues for lot data is not available in OpenItemsDataService.");
  })());
  assertionIssues = computed(() => this.openItemsDataService.assertionIssues() ?? (() => {
    this.goBack();
    throw new Error("Assertion issues data is not available in OpenItemsDataService.");
  })());
  jobIssueVendors = computed(() => this.openItemsDataService.jobIssueVendors() ?? (() => {
    this.goBack();
    throw new Error("Job issue vendors data is not available in OpenItemsDataService.");
  })());
  vendors = computed(() => this.openItemsDataService.vendors() ?? (() => {
    this.goBack();
    throw new Error("Vendor data is not available in OpenItemsDataService.");
  })());
  userVendorContact = computed(() => this.openItemsDataService.userVendorContacts() ?? (() => {
    this.goBack();
    throw new Error("User vendor contact data is not available in OpenItemsDataService.");
  })());

  lotId = computed(() => this.lot().id);
  communityId = computed(() => this.openItemsDataService.financialCommunityId());
  headerSubtitle = computed(() => `Lot ${this.lot().lotBlock}${this.lot().streetAddress ? `, ${this.lot().streetAddress}` : ""}, ${this.lot().salesCommunityName.trim()}`);
  externalFinancialCommunityId = computed(() => this.lot().externalFinancialCommunityId);

  protected issuesWithDetails = computed(() => {
    const actionableJobIssues = this.actionableJobIssues();
    const allJobIssuesForLot = this.allJobIssuesForLot();
    const areaMap = new Map(this.areas().map(area => [area.identifier, area]));

    const media = this.media();
    const assertionIssues = this.assertionIssues();
    const jobs = this.jobs();
    const jobIssueVendors = this.jobIssueVendors();
    const vendors = this.vendors();
    const userVendorContacts = this.userVendorContact();
    const shouldProcessAncestorIssues = true;

    return actionableJobIssues.map(issue => this.jobIssueService.createIssueWithDetails(
      issue,
      areaMap,
      media,
      assertionIssues,
      jobs,
      jobIssueVendors,
      vendors,
      userVendorContacts,
      allJobIssuesForLot,
      shouldProcessAncestorIssues
    ));
  });

  filteredIssues = computed(() => {
    const filtered = this.filterService.applyFilters(this.issuesWithDetails());
    return this.itemSelectionIssuesMapping()[this.currentTab()](filtered, false);
  });

  selectedIssues = computed(() => this.filteredIssues().filter(issue => !!this.issueCheckedMap()[issue.identifier]));

  areAllIssuesSelected = computed(() => this.filteredIssues().length > 0 && this.selectedIssues().length === this.filteredIssues().length);

  loading = computed(() => this.openItemsDataService.isLoadingJobIssues());
  isRefreshingInBackground = computed(() => this.openItemsDataService.isRefreshingInBackground());
  dataUpdatedPulse = computed(() => this.openItemsDataService.dataUpdatedPulse());

  selectedFiltersCount = computed(() => this.filterService.selectedCount());
  activeFilterChips = computed(() => this.filterService.activeChips());

  allSelectedIssuesAreUnassigned = computed(() =>
    this.selectedIssues().length > 0
    && this.selectedIssues().every(issue => issue.isAreaUnassigned === true)
  );

  footerButtons = computed<HorizontalButton[]>(() => {
    const dataLoaded = this.loading() === false;
    return [{
      outlined: true,
      label: "More Actions",
      severity: "secondary",
      styleClass: "bqa-primary-btn",
      disabled: dataLoaded ? this.selectedIssues().length === 0 : true,
      action: () => {
        this.showMoreActionsSheet.set(true);
      },
      testId: "open-items-btn-more-actions"
    },
    {
      label: this.isSendingToVendor() ? "Sending..." : "Send to Vendor",
      severity: "contrast",
      styleClass: "bqa-secondary-btn",
      loading: this.isSendingToVendor(),
      disabled: dataLoaded ? this.selectedIssues().length === 0 || this.isSendingToVendor() : true,
      action: () => this.onSendToVendor(),
      testId: "open-items-btn-send-to-vendor"
    }];
  });

  moreActionsButtons = computed<VerticalButton[]>(() => {
    if (this.loading()) {
      return [];
    }

    const count = this.selectedIssues().length;
    const buttons: VerticalButton[] = [];

    if (this.allSelectedIssuesAreUnassigned()) {
      buttons.push({
        label: "Assign Room",
        severity: "contrast",
        outlined: true,
        class: "has-divider",
        styleClass: "bqa-primary-btn",
        action: () => this.onAssignRoomForSelected(),
        testId: "cm-btn-assign-room-selected"
      });
    }

    buttons.push(
      {
        label: `Assign ${count} Item${count === 1 ? "" : "s"} to Trade`,
        severity: "contrast",
        outlined: true,
        class: "has-divider",
        styleClass: "bqa-primary-btn",
        action: () => this.onAssignItemsToTrade(),
        testId: "cm-btn-assign-vendor-selected"
      },
      {
        label: `Print ${count} Item${count > 1 ? "s" : ""}`,
        severity: "contrast",
        outlined: true,
        styleClass: "bqa-primary-btn",
        action: () => this.onPrintView(),
        testId: "cm-btn-print-items"
      },
      {
        label: "Download CSV",
        severity: "contrast",
        outlined: true,
        styleClass: "bqa-primary-btn",
        action: () => this.onDownloadCsv(),
        testId: "cm-btn-download-csv"
      },
      {
        label: this.isMarkingComplete() ? "Saving..." : "Mark Complete",
        severity: "contrast",
        outlined: true,
        styleClass: "bqa-primary-btn",
        loading: this.isMarkingComplete(),
        disabled: this.isMarkingComplete(),
        action: () => this.onMarkCompleteSelected(),
        testId: "cm-btn-mark-complete-selected"
      }
    );

    return buttons;
  });

  protected readonly itemSelectionIssuesMapping = computed<Record<OpenItemSelection, (issues: JobIssueWithDetails[], isForPrintView?: boolean, isForCsv?: boolean) => JobIssueWithDetails[]>>(() => ({
    [OpenItemSelection.Room]: (issues, _isForPrintView, isForCsv) => {
      return this.flattenVendorsForIssues(issues, !!isForCsv).sort((a: JobIssueWithDetails, b: JobIssueWithDetails) => {
        if (a.isAreaUnassigned && b.isAreaUnassigned) {
          return (a.id ?? 0) - (b.id ?? 0);
        }

        if (a.isAreaUnassigned) {
          return -1;
        }
        if (b.isAreaUnassigned) {
          return 1;
        }

        if (a.areaName && b.areaName) {
          const areaCompare = a.areaName.localeCompare(b.areaName);
          if (areaCompare !== 0) {
            return areaCompare;
          }
        }

        return (a.id ?? 0) - (b.id ?? 0);
      });
    },
    [OpenItemSelection.Vendor]: (issues, isForPrintView, isForCsv) => {
      return this.flattenVendorsForIssues(issues, !!(isForPrintView || isForCsv)).sort((a: JobIssueWithDetails, b: JobIssueWithDetails) => {
        const aVendor = a.assignedVendors?.[0];
        const bVendor = b.assignedVendors?.[0];

        const aVendorIsSelfAssigned = a.isSelfAssigned;
        const bVendorIsSelfAssigned = b.isSelfAssigned;

        const effectiveVendorAName = aVendorIsSelfAssigned ? "Assigned to Me" : aVendor?.vendorDetails.name || "";
        const effectiveVendorBName = bVendorIsSelfAssigned ? "Assigned to Me" : bVendor?.vendorDetails.name || "";

        const vendorCompare = effectiveVendorAName.localeCompare(effectiveVendorBName);
        if (vendorCompare !== 0) {
          return vendorCompare;
        }

        return (a.id ?? 0) - (b.id ?? 0);
      });
    },
    [OpenItemSelection.Type]: (issues, _isForPrintView, isForCsv) => {
      return this.flattenVendorsForIssues(issues, !!isForCsv).sort((a: JobIssueWithDetails, b: JobIssueWithDetails) => {
        const aType = a.componentName || "";
        const bType = b.componentName || "";
        if (aType && bType) {
          const typeCompare = aType.localeCompare(bType);
          if (typeCompare !== 0) {
            return typeCompare;
          }
        }

        if (aType) {
          return 1;
        }
        if (bType) {
          return -1;
        }

        return (a.id ?? 0) - (b.id ?? 0);
      });
    }
  }));

  async ngOnInit(): Promise<void> {
    const tabParam = this.route.snapshot.queryParamMap.get("tab") as OpenItemSelection | null;
    if (tabParam && Object.values(OpenItemSelection).includes(tabParam)) {
      this.currentTab.set(tabParam);
    }

    const dueParam = this.route.snapshot.queryParamMap.get("due");
    const dueFilterValue = Object.values(DueFilterValue)
      .find(value => value.toLowerCase().replace(/\s/g, "") === dueParam?.toLowerCase().replace(/\s/g, ""));
    if (dueFilterValue) {
      this.filterService.replaceWith(DUE_FILTER_ID, [dueFilterValue]);
    } else {
      this.filterService.clear();
    }
  }

  onIssueChecked(event: boolean, issue: JobIssueWithDetails): void {
    this.issueCheckedMap.update(map => ({ ...map, [issue.identifier]: event }));
  }

  onIssueExpanded(event: boolean, issue: JobIssueWithDetails): void {
    this.unexpandAllIssues();
    this.issuesExpandedMap.update(map => ({ ...map, [issue.identifier]: event }));
  }

  onContactSelectionChange(event: ContactChangeEvent): void {
    this.checkedContactMapPerVendor.update(map => ({
      ...map,
      [event.vendorAssignment.externalVendorId]: event.contacts
    }));
  }

  onSelectAll(): void {
    const allChecked = this.filteredIssues().every(issue => this.issueCheckedMap()[issue.identifier]);
    const newMap = Object.fromEntries(this.filteredIssues().map(issue => [issue.identifier, !allChecked]));
    this.issueCheckedMap.set(newMap);
  }

  onAssignItemsToTrade(): void {
    this.showMoreActionsSheet.set(false);
    this.showAssignItemsToTradeSheet.set(true);
  }

  onFilterChipRemove(chip: ActiveFilterChip): void {
    this.filterService.removeChip(chip.filterId, chip.value);
  }

  onFiltersApplied(selected: FilterSelection): void {
    this.filterService.applyDraft(selected);
  }

  onChipRowWheel(event: WheelEvent): void {
    const target = event.currentTarget as HTMLElement;
    if (target) {
      const hasHorizontalOverflow = target.scrollWidth > target.clientWidth;
      if (hasHorizontalOverflow) {
        event.preventDefault();
        target.scrollLeft += event.deltaY;
      }
    }
  }

  onTabChanged(newTab: OpenItemSelection): void {
    this.unexpandAllIssues();
    this.currentTab.set(newTab);
    this.router.navigateWithPersistedState([], {
      relativeTo: this.route,
      queryParams: { tab: newTab },
      queryParamsHandling: "merge",
      replaceUrl: true
    });
  }

  onAddItem(): void {
    this.showAddNewItemSheet.set(true);
  }

  async onNewItemAdded(issue: JobIssue): Promise<void> {
    this.openItemsDataService.addNewIssue(issue);

    this.toastService.success("Item Added Successfully");
    const isVisible = this.filteredIssues().some(i => i.id === issue.id);
    if (isVisible) {
      this.scrollToAndHighlightIssue(issue.id!);
    }
  }

  onCollapseItemsToggle(areCollapsed: boolean): void {
    this.unexpandAllIssues();
    this.areIssuesCollapsed.set(areCollapsed);
  }

  goBack(): void {
    const communityId = this.openItemsDataService.financialCommunityId();
    const lotId = this.openItemsDataService.lot()?.id;
    this.router.navigateWithPersistedState(["/cm/all-my-lots/community", communityId, "lot", lotId, "summary"]);
  }

  openItemsMenu(): void {
    this.isItemsMenuOpen = true;
  }

  navigateToCompletedItems(): void {
    this.isItemsMenuOpen = false;
    this.router.navigateWithPersistedState([
      "/cm/all-my-lots/community", this.communityId(), "lot", this.lotId(), "completed"
    ]);
  }

  private scrollToAndHighlightIssue(issueId: number, attempt: number = 0): void {
    const maxAttempts = 20;
    this.spotlightIssueId.set(issueId);

    setTimeout(() => {
      const el = document.querySelector(`[data-testid="issue-item-${issueId}"]`);

      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });

        // Issue rows lazy-load their content after mount, which shifts the page; re-scroll
        // once the layout has settled so the target row ends up centered in the viewport.
        setTimeout(() => {
          document
            .querySelector(`[data-testid="issue-item-${issueId}"]`)
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 500);

        setTimeout(() => this.spotlightIssueId.set(null), 2500);
        return;
      }

      // The issue list may not have rendered yet after a fast local load, so retry
      // until the target row exists before scrolling to and highlighting it.
      if (attempt < maxAttempts) {
        this.scrollToAndHighlightIssue(issueId, attempt + 1);
      } else {
        this.spotlightIssueId.set(null);
      }
    }, 500);
  }

  private uncheckAllIssues() {
    const newMap = Object.fromEntries(this.filteredIssues().map(issue => [issue.identifier, false]));
    this.issueCheckedMap.set(newMap);
  }

  private pruneStaleCheckedIssues(filteredIssues: JobIssueWithDetails[]): void {
    const filteredIdentifiers = new Set(filteredIssues.map(issue => issue.identifier));
    const currentMap = this.issueCheckedMap();
    const staleIds = Object.keys(currentMap).filter(id => currentMap[id] && !filteredIdentifiers.has(id));

    if (staleIds.length > 0) {
      const newMap = { ...currentMap };
      for (const id of staleIds) {
        delete newMap[id];
      }
      this.issueCheckedMap.set(newMap);
    }
  }

  private unexpandAllIssues() {
    const newMap = Object.fromEntries(this.filteredIssues().map(issue => [issue.identifier, false]));
    this.issuesExpandedMap.set(newMap);
  }

  private flattenVendorsForIssues(issues: JobIssueWithDetails[], shouldFlattenVendors: boolean): JobIssueWithDetails[] {
    if (!shouldFlattenVendors) {
      return [...issues];
    }

    return issues.flatMap(issue => {
      const vendors = issue.assignedVendors;
      if (vendors && vendors.length > 1) {
        return vendors.map(vendor => ({ ...issue, assignedVendors: [vendor] }));
      }
      return [issue];
    });
  }

  onAssignRoomForSelected(): void {
    if (this.selectedIssues().length === 0) {
      return;
    }

    this.showMoreActionsSheet.set(false);
    this.showAssignRoomSheet.set(true);
  }

  async onRoomAssigned(area: Area): Promise<void> {
    const updatedAreaIdentifier = area.identifier;
    for (const issue of this.selectedIssues()) {
      await this.openItemsDataService.updateIssue(issue, { lotAreaIdentifier: updatedAreaIdentifier }, []);
    }
    const issueCount = this.selectedIssues().length;
    this.toastService.success(`${issueCount} item${issueCount === 1 ? "" : "s"} assigned to ${area.name}`);

    this.uncheckAllIssues();
  }

  onBulkVendorAssignment() {
    this.uncheckAllIssues();
  }

  async onPrintView() {
    this.showMoreActionsSheet.set(false);

    const routeExtras = {
      state: {
        selectedIssues: this.itemSelectionIssuesMapping()[this.currentTab()](this.selectedIssues(), true),
        openItemsSelectionType: this.currentTab(),
        totalNumberOfIssues: this.selectedIssues().length
      }
    };

    const communityId = this.communityId();
    this.router.navigateWithPersistedState(["/cm/all-my-lots/community", communityId, "lot", this.lotId(), "open-items", "print"], routeExtras);
    this.uncheckAllIssues();
  }

  async onDownloadCsv() {
    this.showMoreActionsSheet.set(false);

    const lot = this.lot();
    const financialCommunity = lot?.financialCommunityName || "Unknown Financial Community";
    const lotBlock = lot?.lotBlock || "Unknown Lot";
    const lotAddress = lot ? `${lot.streetAddress}, ${lot.salesCommunityName}` : "Unknown Address";
    const constructionManager = this.authService.currentAccount?.name || "Unknown Construction Manager";
    const issues = this.itemSelectionIssuesMapping()[this.currentTab()](this.selectedIssues(), false, true);

    const openItemsCsvRows: OpenItemsCsvRow[] = issues.map(issue => {
      const rawVendor = issue.assignedVendors?.[0]?.vendorDetails.name;

      return {
        bqStep: issue.buildQualityStep !== null && issue.buildQualityStep !== undefined
          ? getBqStepDisplay(issue.buildQualityStep)
          : "N/A",
        financialCommunity,
        lotBlock,
        lotAddress,
        constructionManager,
        dateOfInspection: issue.createdAt ? formatDate(issue.createdAt, "MM/dd/yyyy", "en-US") : "N/A",
        dueDate: issue.dueDate ? formatDate(issue.dueDate, "MM/dd/yyyy", "en-US") : "N/A",
        assignedTo: rawVendor ? this.titleCasePipe.transform(rawVendor) : "Unassigned",
        area: issue.areaName || "Unassigned",
        component: issue.componentName || "N/A",
        description: issue.text || "N/A"
      };
    });

    this.openItemsCsvService.downloadOpenItemsCsv(lotBlock, openItemsCsvRows);
    this.uncheckAllIssues();
  }


  async onSendToVendor(): Promise<void> {
    if (this.isSendingToVendor()) {
      return;
    }

    if (navigator.onLine === false) {
      this.toastService.warn("Cannot send issues to vendor while offline.");
      return;
    }


    this.isSendingToVendor.set(true);

    const contactMap = this.checkedContactMapPerVendor();
    const vendorGroups = new Map<number, SendToVendorGroup>();
    const noAssignedVendorResult: SendToVendorResult = { vendorId: -1, success: false, vendorName: "N/A", contactCount: 0 };
    const invalidVendorEmailResult: SendToVendorResult = { vendorId: -2, success: false, vendorName: "N/A", contactCount: 0 };

    const successMap: Record<number, SendToVendorResult> = {};

    for (const selectedIssue of this.selectedIssues()) {
      if (selectedIssue.assignedVendors.length === 0) {
        successMap[noAssignedVendorResult.vendorId] = noAssignedVendorResult;
        continue;
      }

      for (const assignment of selectedIssue.assignedVendors) {
        const vendorId = assignment.externalVendorId;
        if (vendorGroups.has(vendorId) === false) {
          vendorGroups.set(vendorId, { issues: [], vendorAssignment: assignment, contacts: [] });
        }
        const group = vendorGroups.get(vendorId)!;
        group.issues.push(selectedIssue);

        const seenIds = new Set(group.contacts.map(c => c.id));
        for (const contact of contactMap[vendorId] ?? []) {
          if (seenIds.has(contact.id) === false) {
            group.contacts.push(contact);
            seenIds.add(contact.id);
          }
        }
      }
    }

    for (const [vendorId, group] of vendorGroups) {
      const vendorAssignments = group.issues.map(i => i.assignedVendors.find(v => v.externalVendorId === vendorId)!);
      const vendorName = (group.vendorAssignment.vendorDetails.doingBusinessAs || group.vendorAssignment.vendorDetails.name);
      const totalContactsCountForVendor = vendorAssignments.flatMap(a => a.vendorDetails.userVendorContacts ?? []).length;
      const selectedContactsCountForVendor = group.contacts.length;

      if (selectedContactsCountForVendor > 0) {
        if (group.contacts.some(c => !isValidEmail(c.email ?? ""))) {
          successMap[invalidVendorEmailResult.vendorId] = { ...invalidVendorEmailResult, vendorName, contactCount: group.contacts.length };
        } else {
          const success = await this.openItemsDataService.sendIssuesToVendor(group.issues, vendorAssignments, group.vendorAssignment.vendorDetails as VendorDTO, group.contacts);
          successMap[vendorId] = { vendorId, success, vendorName, contactCount: group.contacts.length };
        }
      } else if (totalContactsCountForVendor === 0) {
        successMap[vendorId] = { vendorId, success: false, vendorName, contactCount: totalContactsCountForVendor };
      }
    }

    const allSuccessful = Object.values(successMap).every(entry => entry.success);
    if (allSuccessful) {
      this.toastService.success("All issues were successfully sent to selected vendors.");
    } else {
      const successSends = [],
        failedSends = [],
        warningSends = [];

      for (const [key, entry] of Object.entries(successMap)) {
        if (entry.success) {
          successSends.push(entry.vendorName);
        } else if (key === noAssignedVendorResult.vendorId.toString()) {
          warningSends.push("at least one selected issue");
        } else if (entry.contactCount === 0) {
          warningSends.push(entry.vendorName);
        } else if (key === invalidVendorEmailResult.vendorId.toString()) {
          failedSends.push(`${entry.vendorName}. At least one email is invalid.`);
        } else {
          failedSends.push(entry.vendorName);
        }
      }
      if (successSends.length > 0) {
        this.toastService.success(`Successfully sent issues to ${successSends.join(", ")}`);
      }
      if (warningSends.length > 0) {
        this.toastService.warn(`No contacts defined for ${warningSends.join(", ")}`);
      }
      if (failedSends.length > 0) {
        this.toastService.error(`Failed to send issues to ${failedSends.join(", ")}`);
      }
    }

    this.isSendingToVendor.set(false);
    this.uncheckAllIssues();
  }

  async onMarkCompleteSelected(): Promise<void> {
    if (this.isMarkingComplete()) {
      return;
    }

    this.isMarkingComplete.set(true);

    const selectedIssues = this.selectedIssues();
    await Promise.all(selectedIssues.map(issue => this.openItemsDataService.updateIssue(issue, { isComplete: true }, [])));

    this.isMarkingComplete.set(false);
    this.showMoreActionsSheet.set(false);
    this.uncheckAllIssues();
  }
}